import { useCallback, useMemo } from "react";
import type { BaseStoredNote } from "../types";
import { loadBaseNotes, saveBaseNotes } from "../helpers/storage";
import { hasDisplayBalance } from "../helpers/cachedBalance";

interface UseBaseInboxOrNoteSyncParams {
  address: string;
  notes: BaseStoredNote[];
  setNotes: (notes: BaseStoredNote[]) => void;
}

export function useBaseInboxOrNoteSync({
  address,
  notes,
  setNotes,
}: UseBaseInboxOrNoteSyncParams) {
  const spendableNotes = useMemo(
    () =>
      notes
        .filter(
          (n) =>
            !n.spent &&
            Number.isInteger(n.note.leafIndex) &&
            n.note.leafIndex >= 0,
        )
        .map((n) => n.note),
    [notes],
  );

  const privateBalance = useMemo(
    () =>
      notes
        .filter(hasDisplayBalance)
        .reduce((sum, n) => sum + n.note.value, 0n),
    [notes],
  );

  const syncNotes = useCallback(() => {
    if (!address) return;
    const local = loadBaseNotes(address);
    setNotes(local);
  }, [address, setNotes]);

  const persistNotes = useCallback(
    (next: BaseStoredNote[]) => {
      if (!address) return;
      saveBaseNotes(address, next);
    },
    [address],
  );

  return {
    privateBalance,
    spendableNotes,
    syncNotes,
    persistNotes,
  };
}
